<template>
  <div class="relative flow-root w-full">
    <ul role="list" class="-mb-8">
      <li v-for="(item, index) in items" :key="item.id">
        <div class="relative pb-8">
          <span
            v-if="index !== items.length - 1"
            class="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200"
            aria-hidden="true"
          />
          <div class="relative flex items-start space-x-3">
            <template v-if="responseType(item.type, 'message')">
              <div class="relative mr-4">
                <span
                  v-if="item.status === 'completed' && item.response"
                  class="flex items-center justify-center w-10 h-10 bg-green-500 rounded-full"
                >
                  <CheckIcon class="w-5 h-5 text-white" aria-hidden="true" />
                </span>
                <span
                  v-else
                  class="flex items-center justify-center w-10 h-10 rounded-full bg-gold"
                >
                  <ChatAltIcon
                    class="w-5 h-5 text-tolopea"
                    aria-hidden="true"
                  />
                </span>
              </div>
              <div class="flex-1 min-w-0">
                <div>
                  <div class="text-sm">
                    <a class="text-base font-bold text-vulcan dark:text-gold">{{
                      item.name
                    }}</a>
                  </div>
                  <p class="mt-0.5 text-sm text-mute">{{ item.date }}</p>
                </div>
                <div class="mt-2 text-gray-700 dark:text-white">
                  <p class="italic">
                    {{ item.description }}
                  </p>
                </div>
                <div
                  v-if="item.status === 'completed' && item.response"
                  class="flex flex-col mt-4 space-y-2 text-sm text-gray-700"
                >
                  <h2
                    class="flex flex-row justify-start my-2 space-x-2 text-sm font-bold item-center text-tolopea dark:text-gold"
                  >
                    <ChatAlt2Icon
                      class="w-6 text-green-500 h6"
                      aria-hidden="true"
                    />
                    <span>{{ t("response") }}</span>
                  </h2>
                  <p class="italic dark:text-mute">
                    {{ item.response.message }}
                  </p>
                </div>
                <div v-else class="flex mt-4 text-sm text-gray-700">
                  <button
                    v-if="!item.editing"
                    @click="reply(item)"
                    class="bg-[#1b1c20] text-white rounded-[4px] text-center font-bold text-sm px-6 py-[1px]"
                  >
                    {{ t("reply") }}
                  </button>
                  <div v-else class="w-full">
                    <textarea
                      v-model="editing"
                      class="w-full text-sm font-medium h-[100px] bg-white shadow rounded-md border-1 border-white-sand focus:border-gold focus:ring-0 focus:outline-none ring-0"
                    ></textarea>
                    <div class="mt-4 space-x-2">
                      <button
                        @click="submitMessage(item)"
                        class="bg-green-500 text-white rounded-[4px] text-center font-bold text-sm px-6 py-[1px]"
                      >
                        {{ t("action.submit") }}
                      </button>
                      <button
                        @click="reply(item)"
                        class="bg-gray-400 text-tolopea rounded-[4px] text-center font-bold text-sm px-6 py-[1px]"
                      >
                        {{ t("cancel") }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <template v-else-if="responseType(item.type, 'file')">
              <!-- Icons -->
              <div class="relative mr-4">
                <span
                  v-if="item.status === 'completed' && item.response"
                  class="flex items-center justify-center w-10 h-10 bg-green-500 rounded-full"
                >
                  <CheckIcon class="w-5 h-5 text-white" aria-hidden="true" />
                </span>
                <span
                  v-else
                  class="flex items-center justify-center w-10 h-10 rounded-full bg-gold"
                >
                  <DocumentAddIcon
                    class="w-5 h-5 text-tolopea"
                    aria-hidden="true"
                  />
                </span>
              </div>
              <!--// Icons -->
              <div class="min-w-0 flex-1 py-1.5">
                <div>
                  <div class="text-sm">
                    <a class="text-base font-bold text-vulcan dark:text-gold">{{
                      item.name
                    }}</a>
                  </div>
                  <p class="mt-0.5 text-sm text-mute">{{ item.date }}</p>
                </div>
                <div class="mt-2 text-gray-700 dark:text-white">
                  <p class="italic">
                    {{ item.description }}
                  </p>
                  <!--- Extra -->
                  <ul
                    class="list-disc my-4 list-inside p-4 bg-white rounded-lg dark:text-white dark:bg-[#2e2d38] shadow-sm border-[1px] dark:border-[#2b273d] border-gray-100"
                    v-if="item?.list?.extra && item.list.extra.length > 0"
                  >
                    <li
                      class="text-sm"
                      v-for="(extra, idx) in item.list.extra"
                      :key="ids"
                    >
                      {{ extra.name }}
                    </li>
                  </ul>
                  <!---// Extra -->
                </div>
                <div
                  v-if="item.status === 'completed' && item.response"
                  class="mt-4 lg:w-[420px] text-sm text-green-500 flex flex-col space-y-2"
                >
                  {{ t("completed") }}
                </div>
                <div v-else class="flex mt-4 text-sm text-gray-700">
                  <button
                    v-if="!item.editing"
                    @click="fetchFile(item)"
                    class="bg-[#1b1c20] text-white rounded-[4px] text-center font-bold text-sm px-6 py-[3px]"
                  >
                    <span v-if="item?.list?.type === 'link_file'">{{
                      t("download")
                    }}</span>
                    <span v-else>{{ t("drop") }}</span>
                  </button>
                  <div v-else class="w-full">
                    <file-pond
                      name="request-file-bdc"
                      ref="pond"
                      :label-idle="t('drop2')"
                      :allow-multiple="false"
                      accepted-file-types="application/pdf, image/*"
                      credits="{}"
                      @init="handleFilePondInit(item)"
                    />

                    <div class="flex flex-row space-x-2">
                      <button
                        @click="cancel(item)"
                        class="bg-gray-400 text-tolopea rounded-[4px] text-center font-bold text-sm px-6 py-[3px]"
                      >
                        {{ t("cancel") }}
                      </button>
                      <button
                        @click="complete(item)"
                        class="bg-green-500 text-white rounded-[4px] text-center font-bold text-sm px-6 py-[3px]"
                      >
                        {{ t("complete") }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <template v-else-if="responseType(item.type, 'confirmation')">
              <div>
                <div class="relative px-1">
                  <div
                    class="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full ring-8 ring-white"
                  >
                    <TagIcon class="w-5 h-5 text-gray-500" aria-hidden="true" />
                  </div>
                </div>
              </div>
              <div class="flex-1 min-w-0 py-0">
                <div class="text-sm leading-8 text-gray-500">
                  <span class="mr-0.5">
                    <a class="font-medium text-gray-900 dark:text-gold">{{
                      item.name
                    }}</a>
                    {{ " " }}
                    added tags
                  </span>
                  {{ " " }}
                  <span class="mr-0.5">
                    <template v-for="tag in item.tags" :key="tag.name">
                      <a
                        :href="tag.href"
                        class="relative inline-flex items-center rounded-full border border-gray-300 px-3 py-0.5 text-sm"
                      >
                        <span
                          class="absolute flex items-center justify-center flex-shrink-0"
                        >
                          <span
                            :class="[tag.color, 'h-1.5 w-1.5 rounded-full']"
                            aria-hidden="true"
                          />
                        </span>
                        <span class="ml-3.5 font-medium text-gray-900">{{
                          tag.name
                        }}</span>
                      </a>
                      {{ " " }}
                    </template>
                  </span>
                  <span class="whitespace-nowrap">{{ item.date }}</span>
                </div>
              </div>
            </template>
          </div>
        </div>
      </li>
    </ul>
  </div>
  <Alert
    v-if="alert.show"
    :alert="alert"
    @dismiss="dismiss"
    class="fixed top-0 left-0 z-10 w-full"
  />
</template>

<script>
import {
  AnnotationIcon,
  ChatAlt2Icon,
  ChatAltIcon,
  CheckIcon,
  DocumentAddIcon,
  TagIcon,
  UserCircleIcon,
} from "@heroicons/vue/solid";
import { onMounted, reactive, ref, toRefs } from "vue";
import Alert from "./Alert";
import { useI18n } from "vue-i18n";
import vueFilePond, { setOptions } from "vue-filepond";
//import "filepond-plugin-pdf-preview/dist/filepond-plugin-pdf-preview.min.css";
import "filepond/dist/filepond.min.css";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";

import { responseType } from "../util";
//import FilePondPluginPdfPreview from "filepond-plugin-pdf-preview";

const FilePond = vueFilePond(
  //FilePondPluginPdfPreview,
  FilePondPluginFileValidateType
);

export default {
  name: "CommunicationFeed",
  components: {
    FilePond,
    Alert,
    DocumentAddIcon,
    ChatAlt2Icon,
    AnnotationIcon,
    ChatAltIcon,
    TagIcon,
    CheckIcon,
    UserCircleIcon,
  },
  props: {
    application: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const pond = ref(null);
    const { t } = useI18n();
    const items = ref([]);
    const item = ref({});
    const state = reactive({
      alert: {
        show: false,
        type: "success",
        message: "Successfully updated.",
      },
      editing: "",
    });

    onMounted(() => {
      axios.get(`/applications/${props.application.app_id}/requests`).then((res) => {
        items.value = res.data.data;
      });
    });

    const reply = (obj) => {
      obj.editing = !obj.editing;
      item.value = obj;
    };

    const cancel = (obj) => {
      obj.editing = !obj.editing;
      item.value = {};
    };

    const complete = async (obj) => {
      try {
        const res = await axios.patch(`/applications/${props.application.app_id}/requests`, {
          id: obj.id,
          type: "status",
        });
        const idx = items.value.findIndex((item) => obj.id === item.id);
        items.value[idx] = res.data.data;
        obj.editing = !obj.editing;
      } catch (e) {
        state.alert = {
          show: true,
          type: "error",
          message: e.message,
        };
      }
    };

    const submitMessage = async (item) => {
      if (state.editing.length > 0) {
        try {
          const { data } = await axios.patch(`/applications/${props.application.app_id}/requests`, {
            message: state.editing,
            id: item.id,
          });

          const objIndex = items.value.findIndex((obj) => obj.id === item.id);
          items.value[objIndex] = data.data;

          state.alert = {
            show: true,
            type: "success",
            message: "Successfully updated.",
          };
          state.editing = "";
        } catch (e) {
          state.alert = {
            show: true,
            type: "error",
            message: e.message,
          };
        } finally {
        }
      }
    };

    const dismiss = () => {
      state.alert.show = false;
      state.alert.message = "";
    };

    const handleFilePondInit = (item) => {
      /*setOptions({
                                                                      allowPdfPreview: true,
                                                                      pdfPreviewHeight: 600,
                                                                      pdfComponentExtraParams: 'toolbar=0&view=fit&page=1'
                                                                  }*/
    };

    const fpUpload = async (
      fieldName,
      file,
      metadata,
      load,
      error,
      progress,
      abort
    ) => {
      const { data } = await axios.post("/assets/signed");
      const headers = data.headers;
      const options = {
        headers,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          progress(percentCompleted, progressEvent.loaded, progressEvent.total);
        },
      };
      await axios.put(data.signedUrl, file, options);

      await axios.patch(`/applications/${props.application.app_id}/requests`, {
        id: item.value.id,
        type: item.value.type,
        key: data.key,
      });

      load(data.key);

      // Should expose an abort method so the request can be cancelled
      return {
        abort: () => {
          // Let FilePond know the request has been cancelled
          abort();
        },
      };
    };

    const fpRevert = async (uniqueFileId, load, error) => {
      console.log(uniqueFileId, item.value.id);

      await axios.delete(`/applications/${props.application.app_id}/requests`, {
        headers: {},
        data: {
          id: item.value.id,
          key: uniqueFileId,
        },
      });

      load();
    };

    setOptions({
      /*allowPdfPreview: true,
                                                                  pdfPreviewHeight: 600,
                                                                  pdfComponentExtraParams: 'toolbar=0&view=fit&page=1',*/
      server: {
        process: fpUpload,
        revert: fpRevert,
      },
    });

    const fetchFile = async (obj) => {
      obj.editing = !obj.editing;
      item.value = obj;
      if (obj?.list?.content) {
        window.open(obj.list.content);
      }
    };

    return {
      t,
      pond,
      fetchFile,
      cancel,
      responseType,
      complete,
      dismiss,
      items,
      handleFilePondInit,
      submitMessage,
      reply,
      ...toRefs(state),
    };
  },
};
</script>
